import React, { useState, useEffect } from "react";
import { Modal, Form, Select, Checkbox, Button, Space, message } from "antd";
import { MemberFieldService } from '../../Services/MemberFieldService';
import { mapDynamicInputLabel } from '../../Mappers/mapDynamicInputLabel';
import { MemberFieldTypes } from '../../Types/MemberFieldTypes';
import { WebApiModelsMemberField } from '../../Repository/eventbookyapi';

export interface AddFieldModalProps {
    open: boolean;
    onCancel: () => void;
    onFieldAdded: () => Promise<void>;
    GetText: (key: string) => string;
    currentFields?: (WebApiModelsMemberField | any)[];
}


const AddFieldModal: React.FC<AddFieldModalProps> = ({ open, onCancel, onFieldAdded, GetText, currentFields = [] }) => {
    const [form] = Form.useForm();
    const [visibleAdmin, setVisibleAdmin] = useState(false);
    const [visiblePublic, setVisiblePublic] = useState(false);
    const [mandatoryAdmin, setMandatoryAdmin] = useState(false);
    const [mandatoryPublic, setMandatoryPublic] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [unusedFields, setUnusedFields] = useState<any[]>([]);
    const [availableFields, setAvailableFields] = useState<{ value: number, label: string }[]>([]);
    useEffect(() => {
        if (open) {
            MemberFieldService.GetUnusedMemberFields().then(fields => {
                setUnusedFields(fields);
                const mapped = fields
                    .map((f: any) => ({
                        // Same clarified name as in the field list on the member form settings page
                        label: f.columnType === MemberFieldTypes.Membership
                            ? GetText('MemberFormSettings_MembershipFieldName')
                            : mapDynamicInputLabel(f.columnType, fields, f.id) ?? "",
                        value: f.columnType
                    }));
                setAvailableFields(mapped);
            });
        } else {
            setAvailableFields([]);
            setUnusedFields([]);
        }
    }, [open]);

    const handleFinish = async (values: any) => {
        if (!visibleAdmin && !visiblePublic) {
            setError(GetText('MemberFieldSettings_VisibleRequired'));
            return;
        }
        setError(null);
        setLoading(true);

        try {
            const fieldObj = unusedFields.find(f => f.columnType === values.fieldType);
            if (fieldObj) {
                let columnOrder: number | undefined = undefined;
                if (fieldObj.columnType === MemberFieldTypes.Membership) {
                    columnOrder = 1;
                }
                await MemberFieldService.AddNewField({
                    columnType: fieldObj.columnType,
                    nameAdmin: fieldObj.nameAdmin,
                    namePublic: fieldObj.namePublic,
                    visibleInAdmin: visibleAdmin,
                    visiblePublic: visiblePublic,
                    mandatoryAdmin: mandatoryAdmin,
                    mandatoryPublic: mandatoryPublic,
                    columnOrder,
                });

                message.success(GetText('Common_Saved'));
                await onFieldAdded();
                form.resetFields();
                setVisibleAdmin(false);
                setVisiblePublic(false);
                setMandatoryAdmin(false);
                setMandatoryPublic(false);
            }
        } catch (error) {
            setError('Failed to add field. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={GetText('MemberFieldSettings_AddField')}
            open={open}
            onCancel={onCancel}
            footer={null}
            destroyOnClose
        >
            <Form layout="vertical" form={form} onFinish={handleFinish}>
                {error && <div style={{ color: 'red', marginBottom: 8 }}>{error}</div>}
                <Form.Item label={GetText('MemberFields_FieldName')} name="fieldType" rules={[{ required: true, message: GetText('MemberFieldSettings_SelectField') }]}>
                    <Select placeholder={GetText('MemberFieldSettings_SelectField')}>
                        {availableFields.map(f => (
                            <Select.Option key={f.value} value={f.value}>{f.label}</Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item>
                    <Checkbox checked={visibleAdmin} onChange={e => setVisibleAdmin(e.target.checked)}>
                        {GetText('MemberFields_VisibleInAdmin')}
                    </Checkbox>
                </Form.Item>
                <Form.Item>
                    <Checkbox checked={visiblePublic} onChange={e => setVisiblePublic(e.target.checked)}>
                        {GetText('MemberFields_VisibleInPublic')}
                    </Checkbox>
                </Form.Item>
                <Form.Item>
                    <Checkbox checked={mandatoryAdmin} onChange={e => setMandatoryAdmin(e.target.checked)}>
                        {GetText('MemberFields_MandatoryAdmin') || 'Obligatorisk admin'}
                    </Checkbox>
                </Form.Item>
                <Form.Item>
                    <Checkbox checked={mandatoryPublic} onChange={e => setMandatoryPublic(e.target.checked)}>
                        {GetText('MemberFields_MandatoryPublic') || 'Obligatorisk medlem'}
                    </Checkbox>
                </Form.Item>
                <Form.Item>
                    <Space>
                        <Button htmlType="submit" type="primary" loading={loading}>{GetText('MemberFieldSettings_AddField')}</Button>
                        <Button onClick={onCancel} disabled={loading}>{GetText('AddMember_Cancel')}</Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AddFieldModal;
